import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface UserMacroGoal {
  id: string;
  user_id: string;
  macro_goal_id: string;
  is_primary: boolean;
  created_at: string;
}

export interface UserMicroGoal {
  id: string;
  user_id: string;
  micro_goal_id: string;
  created_at: string;
}

export const useUserMacroGoals = () => {
  return useQuery({
    queryKey: ['user-macro-goals'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_macro_goals')
        .select('*')
        .eq('user_id', user.id)
        .order('is_primary', { ascending: false });

      if (error) throw error;
      return data as UserMacroGoal[];
    },
  });
};

export const useUserMicroGoals = () => {
  return useQuery({
    queryKey: ['user-micro-goals'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_micro_goals')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data as UserMicroGoal[];
    },
  });
};

interface SaveMacroGoalsInput {
  macroGoalIds: string[];
  microGoalIds?: string[];
}

export const useSaveUserGoals = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ macroGoalIds, microGoalIds = [] }: SaveMacroGoalsInput) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Delete existing macro goals
      const { error: deleteMacroError } = await supabase
        .from('user_macro_goals')
        .delete()
        .eq('user_id', user.id);

      if (deleteMacroError) throw deleteMacroError;

      // Delete existing micro goals
      const { error: deleteMicroError } = await supabase
        .from('user_micro_goals')
        .delete()
        .eq('user_id', user.id);

      if (deleteMicroError) throw deleteMicroError;

      // Insert new macro goals
      if (macroGoalIds.length > 0) {
        const macroGoalsToInsert = macroGoalIds.map((id, index) => ({
          user_id: user.id,
          macro_goal_id: id,
          is_primary: index === 0,
        }));

        const { error: insertMacroError } = await supabase
          .from('user_macro_goals')
          .insert(macroGoalsToInsert);

        if (insertMacroError) throw insertMacroError;
      }

      // Insert new micro goals
      if (microGoalIds.length > 0) {
        const microGoalsToInsert = microGoalIds.map(id => ({
          user_id: user.id,
          micro_goal_id: id,
        }));

        const { error: insertMicroError } = await supabase
          .from('user_micro_goals')
          .insert(microGoalsToInsert);

        if (insertMicroError) throw insertMicroError;
      }

      return { macroGoalIds, microGoalIds };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-macro-goals'] });
      queryClient.invalidateQueries({ queryKey: ['user-micro-goals'] });
    },
    onError: (error) => {
      console.error('Error saving goals:', error);
      toast.error('Failed to save goals. Please try again.');
    },
  });
};

export const useUserGoalDomainWeights = () => {
  return useQuery({
    queryKey: ['user-goal-domain-weights'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .rpc('get_user_goal_domain_weights', { p_user_id: user.id });

      if (error) throw error;
      return data as Record<string, number> | null;
    },
  });
};
